import { Td, Tr } from '@patternfly/react-table'
import * as React from 'react'
import { useCallback, useMemo } from 'react'

import { PatientBirthSex } from '@app/domain/patient/components/patient-birth-sex.component'
import type { IPatient } from '@app/domain/patient/patient.interface'
import PersonName from '@app/domain/person/components/person-name.component'
import { ActionLink } from '@app/utils/styles/action-link'
import { SmallButton } from '@app/utils/styles/small-button'

import type { PatientActionItem } from './patient-list.interface'

interface Props {
  actions?: PatientActionItem[]
  columns: React.ReactElement[]
  isSelected?: boolean
  patient: IPatient
  onActionSelected?: (key: string, patient: IPatient) => void
  onPatientSelected: (patient: IPatient) => void
}

export const PatientListRow = ({
  actions,
  columns,
  isSelected,
  patient,
  onActionSelected,
  onPatientSelected,
}: Props) => {
  const handleActionSelected = useCallback(
    (event) => {
      event.preventDefault()
      const item = event.target.getAttribute('value')
      onActionSelected?.(item, patient)
    },
    [onActionSelected, patient],
  )

  const actionItems = useMemo(() => {
    if (actions?.length > 0) {
      return actions.map((action) => {
        return (
          <SmallButton
            style={{ marginRight: 5 }}
            variant="secondary"
            onClick={handleActionSelected}
            key={action.key}
            value={action.key}
          >
            {action.label}
          </SmallButton>
        )
      })
    } else {
      return []
    }
  }, [actions, handleActionSelected])

  const handlePatientSelected = useCallback(() => {
    onPatientSelected(patient)
  }, [onPatientSelected, patient])

  return (
    <Tr isHoverable isRowSelected={isSelected}>
      <Td>
        <ActionLink onClick={handlePatientSelected}>
          {patient.medicalRecordNumber}
        </ActionLink>
      </Td>
      <Td>
        <ActionLink onClick={handlePatientSelected}>
          <PersonName namedPerson={patient} />
        </ActionLink>
      </Td>
      <Td>
        <PatientBirthSex patient={patient} />
      </Td>
      <Td>{patient.dateOfBirth}</Td>
      {columns?.map((column, index) => {
        return <Td key={index}>{column}</Td>
      })}
      {actionItems.length > 0 ? <Td>{actionItems}</Td> : <Td />}
    </Tr>
  )
}
